import { ActionIcon, Checkbox, Group, SegmentedControl, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { slopeErrors, type MeasureRoom, type MeasureSlope, type SlopeMeasuredAlong } from '@erp/shared';

/** Кнопки стен помещаются в строку телефона до шести; дальше — выпадающий список. */
const MAX_WALL_BUTTONS = 6;

const ACCENT = 'var(--mantine-primary-color-filled)';
const LINE = 'var(--mantine-color-text)';
const DIM = 'var(--mantine-color-dimmed)';

/**
 * Разрез мансарды с подсвеченным размером, который нужно снять. Прорабу,
 * впервые меряющему скат, словами «длина ската по потолку» не всё понятно —
 * картинка снимает вопрос, от какой точки до какой тянуть рулетку.
 */
function SlopeSketch({ along, broken }: { along: SlopeMeasuredAlong; broken: boolean }) {
  const floorY = 78;
  const kneeY = 56;
  const topY = 18;
  const wallX = 22;
  const flatX = broken ? 96 : 84;
  const breakPoint: [number, number] = [44, 32];
  const slopePath = broken
    ? `M ${wallX} ${kneeY} L ${breakPoint[0]} ${breakPoint[1]} L ${flatX} ${topY}`
    : `M ${wallX} ${kneeY} L ${flatX} ${topY}`;
  const accentWidth = 3;
  return (
    <svg viewBox="0 0 210 96" width={210} height={96} role="img" aria-label="Что мерить у ската">
      {/* Пол, стена под скатом, скат, плоский потолок и высокая стена. */}
      <line x1={wallX - 6} y1={floorY} x2={200} y2={floorY} stroke={LINE} strokeWidth={1.5} />
      <line x1={wallX} y1={floorY} x2={wallX} y2={kneeY} stroke={LINE} strokeWidth={1.5} />
      <path d={slopePath} fill="none" stroke={along === 'CEILING' ? ACCENT : LINE} strokeWidth={along === 'CEILING' ? accentWidth : 1.5} />
      <line x1={flatX} y1={topY} x2={186} y2={topY} stroke={LINE} strokeWidth={1.5} />
      <line x1={186} y1={topY} x2={186} y2={floorY} stroke={LINE} strokeWidth={1.5} />

      {/* Колено */}
      <line x1={wallX - 10} y1={kneeY} x2={wallX - 10} y2={floorY} stroke={ACCENT} strokeWidth={1} />
      <text x={wallX - 13} y={(kneeY + floorY) / 2 + 3} fontSize={8} fill={ACCENT} textAnchor="end">
        1
      </text>

      {along === 'FLOOR' && (
        <>
          {/* Отвес от конца ската к полу и отступ по полу. */}
          {broken && (
            <line x1={breakPoint[0]} y1={breakPoint[1]} x2={breakPoint[0]} y2={floorY} stroke={DIM} strokeDasharray="2 2" />
          )}
          <line x1={flatX} y1={topY} x2={flatX} y2={floorY} stroke={DIM} strokeDasharray="2 2" />
          <line x1={wallX} y1={floorY + 7} x2={broken ? breakPoint[0] : flatX} y2={floorY + 7} stroke={ACCENT} strokeWidth={accentWidth} />
          {broken && (
            <line x1={breakPoint[0]} y1={floorY + 7} x2={flatX} y2={floorY + 7} stroke={ACCENT} strokeWidth={1.5} />
          )}
        </>
      )}

      <text x={(wallX + flatX) / 2 + 10} y={along === 'FLOOR' ? floorY + 16 : (kneeY + topY) / 2 - 2} fontSize={8} fill={ACCENT}>
        2{broken ? ' и 3' : ''}
      </text>

      {broken && (
        <circle cx={breakPoint[0]} cy={breakPoint[1]} r={2.5} fill={ACCENT}>
          <title>Излом</title>
        </circle>
      )}

      {/* Высота помещения — до плоского потолка. */}
      <line x1={196} y1={topY} x2={196} y2={floorY} stroke={DIM} strokeWidth={1} />
      <text x={193} y={topY - 5} fontSize={8} fill={DIM} textAnchor="end">
        высота помещения
      </text>
    </svg>
  );
}

function legend(along: SlopeMeasuredAlong, broken: boolean): string {
  const second =
    along === 'CEILING'
      ? broken
        ? '2 — по потолку от верха стены до излома, 3 — от излома до плоского потолка'
        : '2 — рулеткой по потолку от верха стены до начала плоского потолка'
      : broken
        ? '2 — по полу от стены до отвеса с излома, 3 — от отвеса с излома до отвеса с начала плоского потолка'
        : '2 — по полу от стены до отвеса с начала плоского потолка';
  return `1 — высота стены под скатом; ${second}.`;
}

/**
 * Скаты потолка мансарды. Номера стен — те же, что в кружках на плане. Как
 * мерить скат, прораб решает на объекте: вдоль вагонки быстрее, а по полу —
 * когда скат зашит, загорожен или до него не дотянуться.
 */
export function SlopesEditor({
  room,
  wallCount,
  canEdit,
  onChange,
}: {
  room: MeasureRoom;
  wallCount: number;
  canEdit: boolean;
  onChange: (slopes: MeasureSlope[]) => void;
}) {
  const slopes = room.slopes ?? [];
  if (slopes.length === 0) return null;
  const errors = slopeErrors(room);
  const options = Array.from({ length: wallCount }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));

  const update = (index: number, patch: Partial<MeasureSlope>) =>
    onChange(slopes.map((s, i) => (i === index ? { ...s, ...patch } : s)));

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Скаты потолка
      </Text>
      {slopes.map((slope, index) => {
        const broken = slope.breakHeight != null;
        const along = slope.measuredAlong;
        const setWall = (v: string | null) => v && update(index, { wall: Number(v) });
        // Пока размеры не введены, «укажите размеры» — не ошибка, а следующий шаг.
        const error = slope.kneeHeight.trim() && slope.length.trim() ? errors[index] : null;
        return (
          <Stack key={index} gap={6} pl="xs" style={{ borderLeft: '2px solid var(--mantine-color-default-border)' }}>
            <Group gap="xs" wrap="wrap" align="flex-end">
              {wallCount <= MAX_WALL_BUTTONS ? (
                <Stack gap={2}>
                  <Text size="xs" c="dimmed">
                    Над стеной
                  </Text>
                  <SegmentedControl size="xs" data={options} value={String(slope.wall)} onChange={setWall} disabled={!canEdit} />
                </Stack>
              ) : (
                <Select
                  size="xs"
                  label="Над стеной"
                  data={options}
                  value={String(slope.wall)}
                  onChange={setWall}
                  allowDeselect={false}
                  disabled={!canEdit}
                  w={96}
                />
              )}
              <Stack gap={2}>
                <Text size="xs" c="dimmed">
                  Скат мерили
                </Text>
                <SegmentedControl
                  size="xs"
                  value={along}
                  onChange={(v) => update(index, { measuredAlong: v as SlopeMeasuredAlong })}
                  data={[
                    { value: 'CEILING', label: 'по потолку' },
                    { value: 'FLOOR', label: 'по полу' },
                  ]}
                  disabled={!canEdit}
                />
              </Stack>
              <Checkbox
                size="xs"
                label="Ломаный"
                checked={broken}
                onChange={(e) =>
                  update(index, e.currentTarget.checked ? { breakHeight: '', breakLength: '' } : { breakHeight: null, breakLength: null })
                }
                disabled={!canEdit}
                mb={6}
              />
              {canEdit && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  onClick={() => onChange(slopes.filter((_, i) => i !== index))}
                  aria-label="Удалить скат"
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>

            <Group gap="sm" wrap="wrap" align="flex-start">
              <SlopeSketch along={along} broken={broken} />
              <Stack gap={6} style={{ flex: '1 1 220px' }}>
                <Group gap="xs" wrap="wrap">
                  <TextInput
                    size="xs"
                    label="1. Стена под скатом, м"
                    placeholder="1,2"
                    inputMode="decimal"
                    value={slope.kneeHeight}
                    onChange={(e) => update(index, { kneeHeight: e.currentTarget.value })}
                    disabled={!canEdit}
                    w={140}
                  />
                  <TextInput
                    size="xs"
                    label={`2. ${along === 'CEILING' ? 'По потолку' : 'По полу'}${broken ? ' до излома' : ''}, м`}
                    placeholder="1,8"
                    inputMode="decimal"
                    value={slope.length}
                    onChange={(e) => update(index, { length: e.currentTarget.value })}
                    disabled={!canEdit}
                    w={150}
                  />
                </Group>
                {broken && (
                  <Group gap="xs" wrap="wrap">
                    <TextInput
                      size="xs"
                      label="Высота излома, м"
                      placeholder="1,9"
                      inputMode="decimal"
                      value={slope.breakHeight ?? ''}
                      onChange={(e) => update(index, { breakHeight: e.currentTarget.value })}
                      disabled={!canEdit}
                      w={140}
                    />
                    <TextInput
                      size="xs"
                      label={`3. ${along === 'CEILING' ? 'По потолку' : 'По полу'} после излома, м`}
                      placeholder="1,2"
                      inputMode="decimal"
                      value={slope.breakLength ?? ''}
                      onChange={(e) => update(index, { breakLength: e.currentTarget.value })}
                      disabled={!canEdit}
                      w={190}
                    />
                  </Group>
                )}
                <Text size="xs" c="dimmed">
                  {legend(along, broken)}
                </Text>
                {error && (
                  <Text size="xs" c="seal.6">
                    {error}
                  </Text>
                )}
              </Stack>
            </Group>
          </Stack>
        );
      })}
      <Text size="xs" c="dimmed">
        «Высота, м» у помещения — это высота плоской части потолка (или конька, если плоской части нет).
      </Text>
    </Stack>
  );
}

/** Новый скат — над первой стеной, где ската ещё нет: обычно скаты у противоположных стен. */
export function newSlope(room: MeasureRoom, wallCount: number): MeasureSlope {
  const taken = new Set((room.slopes ?? []).map((s) => s.wall));
  const wall = Array.from({ length: wallCount }, (_, i) => i + 1).find((w) => !taken.has(w)) ?? 1;
  const last = room.slopes?.at(-1);
  return {
    wall,
    // Скаты одной крыши обычно одинаковые — подставляем размеры предыдущего.
    kneeHeight: last?.kneeHeight ?? '',
    measuredAlong: last?.measuredAlong ?? 'CEILING',
    length: last?.length ?? '',
    breakHeight: last?.breakHeight ?? null,
    breakLength: last?.breakLength ?? null,
  };
}
